import React, { FC, ElementType, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cookies from 'js-cookie';

import PageFallback from '@/lib/components/page-fallback';
import PageProgress from '@/lib/components/page-progress';
import useWebSocket from '@/lib/hooks/useWebSocket';
import { SocketEvent } from '@/lib/enums/socket-event.enum';

import { AppPaths } from '@/app-constants';
import { profileSelector } from '@/store/selectors/profile-selectors';
import { actions as notificationActions } from '@/store/reducers/notification-reducer';
import { getProfile } from '@/store/actions/profile-actions';
import { getUnreadNotificationsCount } from '@/store/actions/notification-actions';
import { useAppDispatch } from '@/hooks/useAppDispatch';

const SignInPage = React.lazy(
  () => import(/* webpackChunkName: "page_sign-in" */ '@/pages/sign-in'),
);
const HomePage = React.lazy(
  () => import(/* webpackChunkName: "page_home" */ '@/pages/home'),
);
const CalendarEntriesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page_calendar-entries" */ '@/pages/calendar-entries'
    ),
);
const PatientsPage = React.lazy(
  () => import(/* webpackChunkName: "page_patients" */ '@/pages/patients'),
);
const SettingsPage = React.lazy(
  () => import(/* webpackChunkName: "page_settings" */ '@/pages/settings'),
);
const ChatsPage = React.lazy(
  () => import(/* webpackChunkName: "page_chats" */ '@/pages/chats'),
);
const ToDoPage = React.lazy(
  () => import(/* webpackChunkName: "page_to-do" */ '@/pages/to-do'),
);
const CalendarPage = React.lazy(
  () => import(/* webpackChunkName: "page_calendar" */ '@/pages/calendar'),
);
const ProfileView = React.lazy(
  () =>
    import(/* webpackChunkName: "page_profile-view" */ '@/pages/profile-view'),
);

const PrivateRoute: FC<{ element: ElementType }> = ({ element: Element }) => {
  const { session } = useSelector(profileSelector);

  return session ? (
    <Suspense fallback={<PageFallback />}>
      <Element />
    </Suspense>
  ) : (
    <Navigate to={AppPaths.SIGN_IN} />
  );
};

const AppRoutes: FC = () => {
  const { session } = useSelector(profileSelector);
  const dispatch = useAppDispatch();
  const [socket, sapi] = useWebSocket();
  const location = useLocation();

  useEffect(() => {
    if (!session) {
      return;
    }

    sapi.init(cookies.get('access_token'));
  }, [session, sapi]);

  // The bell badge must be right from the first page, not only after the
  // panel was opened once.
  const sessionUserId = session?.id;
  useEffect(() => {
    if (!sessionUserId) {
      return;
    }

    void dispatch(getUnreadNotificationsCount());
  }, [dispatch, sessionUserId]);

  useEffect(() => {
    if (!socket) {
      return;
    }

    socket.on(SocketEvent.NewNotification, (payload) =>
      dispatch(notificationActions.onNotificationReceived(payload)),
    );

    return () => {
      socket.off(SocketEvent.NewMessage);
      socket.off(SocketEvent.NewNotification);
      socket.off(SocketEvent.NewChats);
    };
  }, [dispatch, socket]);

  useEffect(() => {
    // FIXME: checks if users exists and sign out when profile not found(physio deletion case)
    //  but runs on each route change (but not "/" and "sign-in" coz it will be circular reload")
    if (![AppPaths.HOME, AppPaths.SIGN_IN].includes(location.pathname)) {
      dispatch(getProfile());
    }
  }, [dispatch, location]);

  return (
    <Routes>
      {/* Private */}
      <Route
        path={AppPaths.HOME}
        element={<PrivateRoute element={HomePage} />}
      />
      <Route
        path={AppPaths.HOME_ALL}
        element={<PrivateRoute element={HomePage} />}
      />
      <Route
        path={AppPaths.Patients_All}
        element={<PrivateRoute element={PatientsPage} />}
      />
      <Route
        path={AppPaths.CalendarEntries_All}
        element={<PrivateRoute element={CalendarEntriesPage} />}
      />
      <Route
        path={AppPaths.Settings_All}
        element={<PrivateRoute element={SettingsPage} />}
      />
      <Route
        path={AppPaths.CHATS_ALL}
        element={<PrivateRoute element={ChatsPage} />}
      />
      <Route
        path={AppPaths.ToDo_All}
        element={<PrivateRoute element={ToDoPage} />}
      />
      <Route
        path={AppPaths.Calendar_All}
        element={<PrivateRoute element={CalendarPage} />}
      />
      <Route
        path={AppPaths.ProfileView}
        element={<PrivateRoute element={ProfileView} />}
      />

      {/* Public */}
      <Route
        path={AppPaths.SIGN_IN}
        element={
          <Suspense fallback={<PageProgress />}>
            <SignInPage />
          </Suspense>
        }
      />

      {/* Default */}
      <Route path="*" element={<Navigate to={AppPaths.SIGN_IN} replace />} />
    </Routes>
  );
};

export default AppRoutes;
